import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Play, CheckCircle, ArrowRight, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface DemoModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const DemoModal = ({ open, onOpenChange }: DemoModalProps) => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  const features = [
    {
      title: 'Cronograma Visual',
      description: 'Arraste e solte tarefas no Gantt interativo. Visualize dependências e progresso em tempo real.',
      time: '2 min',
    },
    {
      title: 'Controle Financeiro',
      description: 'Receitas, despesas e fluxo de caixa integrados por projeto. Nunca mais perca dinheiro.',
      time: '2 min',
    },
    {
      title: 'IA do Projeto',
      description: 'Pergunte qualquer coisa sobre seus dados. A IA analisa e gera relatórios automaticamente.',
      time: '1 min',
    },
  ];

  useEffect(() => {
    if (open) {
      const interval = setInterval(() => {
        setActiveFeature((prev) => (prev + 1) % features.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [open, features.length]);

  const handleStartTrial = () => {
    onOpenChange(false);
    navigate('/register');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl p-0 overflow-hidden">
        <div className="grid md:grid-cols-5">
          {/* Left side - Video placeholder / Feature showcase */}
          <div className="md:col-span-3 bg-foreground p-6 md:p-8">
            <DialogHeader className="mb-6">
              <DialogTitle className="text-background text-xl md:text-2xl font-bold">
                Veja o Obrim em ação
              </DialogTitle>
            </DialogHeader>

            {/* Feature showcase */}
            <div className="space-y-4">
              {features.map((feature, index) => (
                <div
                  key={index}
                  className={`p-4 rounded-xl transition-all duration-300 cursor-pointer ${
                    activeFeature === index
                      ? 'bg-background/10 border border-background/20'
                      : 'hover:bg-background/5'
                  }`}
                  onClick={() => setActiveFeature(index)}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${
                        activeFeature === index ? 'bg-primary' : 'bg-background/10'
                      }`}
                    >
                      {activeFeature === index ? (
                        <Play className="w-4 h-4 text-primary-foreground" />
                      ) : (
                        <CheckCircle className="w-4 h-4 text-background/60" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="text-background font-semibold text-sm">
                          {feature.title}
                        </h4>
                        <div className="flex items-center gap-1 text-background/50 text-xs">
                          <Clock className="w-3 h-3" />
                          {feature.time}
                        </div>
                      </div>
                      <p
                        className={`text-xs mt-1 transition-all duration-300 ${
                          activeFeature === index
                            ? 'text-background/80 max-h-20'
                            : 'text-background/40 max-h-0 overflow-hidden'
                        }`}
                      >
                        {feature.description}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Progress indicator */}
            <div className="flex gap-2 mt-6">
              {features.map((_, index) => (
                <div
                  key={index}
                  className={`h-1 flex-1 rounded-full transition-all duration-300 ${
                    activeFeature === index ? 'bg-primary' : 'bg-background/20'
                  }`}
                />
              ))}
            </div>
          </div>

          {/* Right side - CTA */}
          <div className="md:col-span-2 p-6 md:p-8 flex flex-col justify-center">
            <div className="space-y-4">
              <div className="text-center md:text-left">
                <p className="text-sm text-muted-foreground mb-1">Tempo total</p>
                <p className="text-3xl font-bold text-foreground">5 min</p>
                <p className="text-xs text-muted-foreground">para configurar tudo</p>
              </div>

              <div className="space-y-2 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span>7 dias grátis</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span>Sem cartão de crédito</span>
                </div>
                <div className="flex items-center gap-2 text-muted-foreground">
                  <CheckCircle className="w-4 h-4 text-primary" />
                  <span>Suporte por WhatsApp</span>
                </div>
              </div>

              <Button
                className="w-full h-12 rounded-xl font-medium"
                onClick={handleStartTrial}
              >
                Começar agora
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>

              <p className="text-[10px] text-center text-muted-foreground">
                Cancele quando quiser, sem compromisso
              </p>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
